/**
 * MobileReadinessSummary — mobile CQC Readiness score hero (Batch 7A).
 * Server-authoritative: score / band / band_label / strong_count / attention_count /
 * recommendations.length / delta / generated_at / latest signoff are rendered
 * verbatim. No client-side math or bucketing.
 *
 * Preserves existing action endpoints:
 *   - GET /dashboard/readiness/pdf                (Export PDF)
 *   - GET /dashboard/board-review/pdf             (Board review pack)
 *   - GET /dashboard/readiness/signoffs/{id}/pdf  (Signed snapshot PDF)
 *   - POST /dashboard/readiness/signoffs          (via existing dialog)
 *
 * Care Worker gate: Sign & Board Review buttons render only when canManage.
 */
import React from "react";
import {
  Award, FileDown, ShieldCheck, CheckCircle2, AlertCircle, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BAND_STYLES = {
  excellent:   { ring: "border-emerald-500", pill: "bg-emerald-100 text-emerald-800 border-emerald-300", bar: "bg-emerald-500", text: "text-emerald-700" },
  strong:      { ring: "border-teal-500",    pill: "bg-teal-100 text-teal-800 border-teal-300",          bar: "bg-teal-500",    text: "text-teal-700" },
  developing:  { ring: "border-amber-500",   pill: "bg-amber-100 text-amber-900 border-amber-300",       bar: "bg-amber-500",   text: "text-amber-700" },
  improvement: { ring: "border-orange-500",  pill: "bg-orange-100 text-orange-900 border-orange-300",    bar: "bg-orange-500",  text: "text-orange-700" },
  significant: { ring: "border-red-500",     pill: "bg-red-100 text-red-800 border-red-300",             bar: "bg-red-600",     text: "text-red-700" },
};

export default function MobileReadinessSummary({
  readiness, latestSignoff, canManage,
  onExportPdf, onBoardReviewPdf, onOpenSignDialog, onDownloadLatestPdf,
}) {
  if (!readiness) return null;
  const bs = BAND_STYLES[readiness.band] || BAND_STYLES.developing;
  const priorityActionsCount = readiness.recommendations?.length || 0;
  const generatedAt = readiness.generated_at
    ? new Date(readiness.generated_at).toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";

  const DeltaIcon = readiness.delta > 0 ? TrendingUp : readiness.delta < 0 ? TrendingDown : Minus;
  const deltaClass =
    readiness.delta > 0 ? "bg-emerald-50 text-emerald-800 border-emerald-300"
    : readiness.delta < 0 ? "bg-red-50 text-red-800 border-red-300"
    : "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="mobile-readiness-summary">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          <Award className="w-3 h-3" /> CQC Inspection Readiness Report
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          AYOBELL Care · Generated {generatedAt}
        </div>
        <div className="text-[11px] text-slate-400 italic">Internal governance and inspection-preparedness assessment</div>
      </div>

      {/* Score hero */}
      <div className="px-4 py-4 flex items-start gap-4">
        <div className={`w-24 h-24 rounded-lg flex items-center justify-center border-4 shrink-0 ${bs.ring}`}>
          <div className="text-center">
            <div className="font-heading text-3xl font-semibold tabular-nums leading-none" data-testid="mobile-readiness-score">
              {readiness.score}
            </div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-1">/ 100</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">AYOBELL CQC Readiness Score</div>
          <div className={`font-heading text-lg font-semibold ${bs.text} mt-0.5`} data-testid="mobile-readiness-band">
            {readiness.band_label || "Developing Readiness"}
          </div>

          {typeof readiness.delta === "number" && (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium" data-testid="mobile-readiness-delta">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${deltaClass}`}>
                <DeltaIcon className="w-3 h-3" /> {readiness.delta > 0 ? "+" : ""}{readiness.delta} pts
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Count chips */}
      <div className="px-4 pb-3 flex flex-wrap items-center gap-2 text-[11px]" data-testid="mobile-readiness-chips">
        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700"><b>{priorityActionsCount}</b> Priority Actions</span>
        <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-800"><b>{readiness.strong_count ?? 0}</b> Areas Performing Strongly</span>
        <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-900"><b>{readiness.attention_count ?? 0}</b> Areas Requiring Attention</span>
      </div>

      {/* Action buttons (single-column, ≥44px, canManage gates Sign & Board Review) */}
      <div className="px-4 pb-3 flex flex-col gap-2">
        <Button
          variant="outline"
          onClick={onExportPdf}
          data-testid="btn-mobile-readiness-export-pdf"
          className="h-11 w-full justify-center"
        >
          <FileDown className="w-4 h-4 mr-1.5" /> Export PDF
        </Button>
        {canManage && (
          <Button
            onClick={onBoardReviewPdf}
            data-testid="btn-mobile-readiness-board-review"
            className="h-11 w-full justify-center bg-sky-700 hover:bg-sky-800 text-white"
          >
            <FileDown className="w-4 h-4 mr-1.5" /> Prepare for Board Meeting
          </Button>
        )}
        {canManage && (
          <Button
            variant="outline"
            onClick={onOpenSignDialog}
            data-testid="btn-mobile-readiness-sign"
            className="h-11 w-full justify-center border-emerald-400 text-emerald-800 hover:bg-emerald-50"
          >
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Sign Readiness Assessment
          </Button>
        )}
      </div>

      {/* Latest signed chip */}
      {latestSignoff && (
        <div
          className="mx-4 mb-3 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-900 px-3 py-2 text-[12px] flex items-start gap-2"
          data-testid="mobile-readiness-signed-chip"
        >
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold">Readiness Assessment Signed</div>
            <div className="text-emerald-800/80 text-[11px] mt-0.5 break-words">
              {latestSignoff.signed_by_name} · {latestSignoff.signed_by_role}
              {" · "}
              {new Date(latestSignoff.signed_at).toLocaleString(undefined, {
                day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
              })}
            </div>
            <button
              type="button"
              onClick={onDownloadLatestPdf}
              className="mt-2 inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 underline text-[11px] min-h-[44px]"
              data-testid="btn-mobile-readiness-download-latest-signed"
            >
              <FileDown className="w-3.5 h-3.5" /> Download signed PDF
            </button>
          </div>
        </div>
      )}

      {/* Disclaimer — full verbatim wording, always visible, never truncated */}
      <div
        className="mx-4 mb-4 bg-sky-50 border border-sky-200 rounded-md p-3 text-[12px] text-sky-900 leading-snug"
        data-testid="mobile-readiness-disclaimer"
      >
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-sky-700" />
          <div>
            <div className="font-semibold mb-1">About this score</div>
            <div>
              This is an AYOBELL internal inspection-readiness assessment. It is not an official CQC rating,
              inspection outcome, approval or endorsement.
            </div>
            <div className="mt-2">
              AYOBELL CQC Inspection Readiness is an internal governance and inspection-preparedness assessment.
              It does not constitute a CQC rating, inspection outcome, regulatory judgement, approval or endorsement.
            </div>
          </div>
        </div>
      </div>

      {/* Management summary (server-authored, verbatim) */}
      {readiness.summary_text && (
        <div className="mx-4 mb-4 border-t border-slate-100 pt-3" data-testid="mobile-readiness-management-summary">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Management Summary</div>
          <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-line break-words">{readiness.summary_text}</p>
        </div>
      )}
    </div>
  );
}
